import { useRef, useState, type ReactNode } from 'react';
import { isRealDate } from '@/components/catalog/form-utils';
/*
 * ⚠️ The quote/invoice document vocabulary lives in ONE module and this file
 * must not grow a second copy of any of it. `DOCUMENT_LINE_AMOUNT_OPTIONS` was
 * inlined in both document forms once and the two labellings diverged on
 * 2026-08-26 — the same customer-facing tax treatment worded two ways across
 * the halves of one conversion. Import; never re-inline.
 */
import {
  DOCUMENT_LINE_AMOUNT_OPTIONS,
  USE_COMPANY_DEFAULT,
  documentLineAmountLabel,
  toDocumentLineAmountType,
} from '@/components/documents/document-form';
import {
  InlineField,
  type InlineFieldOption,
  type InlineSaveResult,
} from '@/components/ui/InlineField';
import { ApiError, api } from '@/lib/api';
import { cx } from '@/lib/cx';
import { CURRENCY_PATTERN, QUOTE_MAX_LENGTHS } from '@/lib/document-validation';
import { denialReason } from '@/lib/permissions';
import { useAuth } from '@/lib/use-auth';
import { currencyOptions, useCurrencyOptions } from '@/lib/use-invoice-options';
import type { Quote, QuoteUpdateInput, UserSummary } from '@/lib/types';
import type { XeroPushReport } from '@/lib/xero';

/**
 * A quote's HEADER fields, edited where they are read.
 *
 * The companies analogue is `CompanyDetailPage`'s inline block and this follows
 * its shape deliberately: `InlineField` owns the interaction, this file owns the
 * contract — which request key each column writes, what its `null` means, and
 * how a refusal on it should read.
 *
 * It renders a bare `<dl>` grid and no `Panel`, no heading and no `<main>`: the
 * page mounts it inside whatever surface it already has.
 *
 * ## What is NOT editable here, and why each one is different
 *
 * Four exclusions, and they are not one rule with four instances — flattening
 * them is how one of them quietly comes back:
 *
 *  1. **`stageId`.** The stage is set from the quote form or by moving the card
 *     on the board. It is DISPLAYED here, read-only, so nobody hunts for a
 *     control that was deliberately not built.
 *
 *     ⚠️ **The REASON changed on 2026-08-29 and the exclusion did not.** This
 *     used to read that accepting a quote raises an invoice and pushes it into
 *     SEED's real Xero accounts, that the API refuses without an
 *     acknowledgement echoing the total (`409
 *     QUOTE_ACCEPTANCE_REQUIRES_CONFIRMATION`), and that an inline picker has
 *     nowhere to put that dialog. Backend release `20260829011913-5fbab06`
 *     removed all of it: **accepting a quote now moves the stage and nothing
 *     else**, and that code cannot fire on any route.
 *
 *     So the argument that kept `stageId` out of here is spent, and whether it
 *     should now become inline is an **open question nobody has answered** —
 *     deliberately not decided while removing the machinery, because a control
 *     that moves a document to Accepted is not something to add as a side
 *     effect of a deletion. Leave it read-only until it is asked for.
 *  2. **The four header totals** (`subTotal`, `discountTotal`, `taxTotal`,
 *     `grandTotal`). They are STORED, with two competing writers: a line write
 *     rewrites all four from the lines, and a header PATCH writes exactly what
 *     it is given and never re-derives. Never write the header and the lines in
 *     one user action. They are shown read-only for the same reason as the
 *     stage.
 *  3. **`xeroQuoteNumber` and `xeroQuoteId`.** The first is written by a
 *     successful push, so a box over it competes with a real identifier — and
 *     it goes stale on unlink, so it can name ANOTHER quote's document. The
 *     second is written by NOTHING, so a hand-typed value is the only thing
 *     that column will ever hold and it will look authoritative while meaning
 *     nothing. Neither is rendered here at all: the authoritative answer is the
 *     link envelope, which the page shows beside its Xero panel, and a bare
 *     number without that envelope beside it is the confident wrong answer.
 *  4. ⚠️ **`accountLead`.** Not a choice this component made — there is no
 *     `account_lead_id` column on `d_quotes` at all. The server resolves
 *     `company_id -> d_companies.account_lead_id` on every read, so the account
 *     lead is EDITED ON THE COMPANY and reassigning an account re-points every
 *     quote it holds immediately. `accountLeadId` in a quote write body is a
 *     **422**, which is why it appears on no quote input type — a key no
 *     payload can express beats a guard somebody can forget. It is DISPLAYED
 *     here, read-only, so this section and `QuoteFormModal` agree on who leads
 *     the account.
 *
 *     ⚠️ It is **NOT a rename of `owner`, and no copy here may read as one**:
 *     they are two different people, measured live on quote 9 (owner Paras
 *     Fatnani, account lead Daryna Kodinets). This section has never rendered
 *     an owner, so nothing was replaced — the account lead is an ADDITION
 *     beside the rest, not a relabelling of a value somebody was already
 *     reading.
 *
 * ## Two writes carry TWO keys, and both are copied rather than reasoned out
 *
 * "One key per PATCH" is the rule and these are its two documented exceptions,
 * both lifted from `QuoteFormModal` because the alternative is a quote left
 * inconsistent with a 200:
 *
 *  - **The dates travel as a pair.** `issueDate > validTill` is a 422 the server
 *    only runs when it can SEE both dates in one body. A PATCH moving only
 *    `issueDate` past a stored `validTill` therefore succeeds and leaves a quote
 *    that stopped being valid before it was raised — nonsense the conversion
 *    would inherit. So either date changing sends both. The same comparison is
 *    mirrored client-side, so the refusal usually happens before the request.
 *  - **Changing the currency clears a pinned rate.** `onCurrencyChange` in the
 *    modal does exactly this. A rate is pinned against a currency; leaving a
 *    GBP pin on a quote that has become USD puts a fabricated conversion on a
 *    document a customer is invoiced against. `null` clears it honestly —
 *    `currency_rate` is genuinely nullable, so null is stored as null rather
 *    than taking a column default the way the two NOT NULL totals would.
 *
 * ## Why there is no read-back check
 *
 * `CompanyDetailPage` compares the saved record against what it sent, because
 * `PATCH /companies/:id` is NOT `.strict()` — it strips an unknown key and
 * answers 200, so a success proves nothing. **Every quote write schema IS
 * `.strict()`**: an unknown key is a 422 naming it. A 200 here therefore does
 * prove the key was accepted, and a read-back would be ceremony rather than
 * safety. Do not copy the company one across without checking that first.
 */

export interface InlineQuoteFieldsProps {
  quote: Quote;
  /**
   * Fires after each successful save, with the record the server returned.
   *
   * ⚠️ **CORRECTED 2026-08-30. A SAVE FROM HERE NOW REACHES XERO.** This read:
   *
   * > *"⚠️ **This component never pushes to Xero.** The page owns that
   * > decision: one push per field would rewrite a live accounting document
   * > once per keystroke-sized edit, and Xero re-numbers a quote on some
   * > updates."*
   *
   * The first clause is still literally true and the rest is not, which is why
   * it is quoted rather than trimmed: **nothing in this file calls a push
   * endpoint** — but the PATCH it makes pushes on the SERVER, inside the same
   * request, so an inline field edit goes to Xero on its own and the page does
   * not own the decision any more. Nobody does; it is a property of the API.
   *
   * The cost that paragraph was protecting against is real and is now the
   * server's to bear: it takes a per-document lock, so rapid field edits
   * serialise rather than race, and each save waits for its own push.
   */
  onSaved: (saved: Quote) => void;
  /**
   * Hands the page the SERVER'S report of what that save did to Xero.
   *
   * ⚠️ **Added 2026-08-30**, and it is the reporting half of the correction
   * above: the push was already happening, and this is what stops it happening
   * silently.
   *
   * ⚠️ **The RAW server report goes up, not a classified outcome** — the same
   * contract `QuoteFormModalProps.onXeroPush` states, and deliberately the same
   * prop name, shape and reasoning. Two spellings of one idea is how this
   * section and that modal come to disagree about the same save. The vocabulary
   * belongs to `@/lib/xero` and the copy belongs to the page, so
   * `autoPushOutcomeOf` is read in ONE place.
   *
   * ⚠️ **It cannot fail or roll back the save.** The PATCH has returned by the
   * time this runs and there is no transaction across the two halves, so
   * nothing built on it may be worded as the save having failed. `undefined` is
   * what an older server, or a rollback, sends, and it is an ordinary answer
   * rather than an error.
   *
   * ⚠️ **Only the NEWEST write in flight reports**, exactly as `onSaved` does —
   * see `writeSeq`. A report from a save that has already been superseded is a
   * statement about a version of the quote nobody is looking at.
   */
  onXeroPush?: (report: XeroPushReport | undefined) => void;
}

/*
 * ⚠️ The server-enforced maximums are IMPORTED as `QUOTE_MAX_LENGTHS`, and this
 * file must not grow a copy of them again. The copy that stood here carried an
 * entry the modal's does not — `quoteNumber: 64` — left over from the hours
 * when that field was editable in this section. It is now allocated by the
 * server and a 422 on both create and PATCH, so the entry pointed at a control
 * that no longer exists. That is the drift this lift exists to prevent, caught
 * on the way past. The reasoning for every absent key lives on
 * `QUOTE_MAX_LENGTHS` itself.
 */

/**
 * The request keys this component sends, and therefore the only paths a 422 may
 * name that it can put on a control.
 *
 * ⚠️ **It is the SEND list, not the render list, and the two differ.** The
 * stage and the four totals are rendered — read-only — and are deliberately
 * absent here: a path naming one of them binds to no editor, so it belongs in
 * the section-level banner rather than being marked on a value nobody can
 * change. A non-empty path that binds to nothing is the worse failure, because
 * it looks bindable.
 */
const WRITE_KEYS = [
  'subject',
  'reference',
  'terms',
  'issueDate',
  'validTill',
  'currency',
  'currencyRate',
  'xeroLineAmountType',
  'xeroTrackingLocation',
  'xeroTrackingTourType',
] as const;

type WriteKey = (typeof WRITE_KEYS)[number];

/** How each write key reads to a person, for a refusal that names another field. */
const FIELD_LABELS: Record<WriteKey, string> = {
  subject: 'Subject',
  reference: 'Customer PO number',
  terms: 'Terms',
  issueDate: 'Issue date',
  validTill: 'Valid till',
  currency: 'Currency',
  currencyRate: 'Exchange rate',
  xeroLineAmountType: 'Tax treatment',
  xeroTrackingLocation: 'Location override',
  xeroTrackingTourType: 'Tour Type override',
};

/** One copy of each message, in the order they were collected. */
function deduped(lines: readonly string[]): string[] {
  return [...new Set(lines)];
}

export function InlineQuoteFields({ quote, onSaved, onXeroPush }: InlineQuoteFieldsProps) {
  const { ability } = useAuth();
  const canUpdate = ability.canUpdate('quotes');
  const readOnlyReason = canUpdate ? null : denialReason(ability, 'update', 'quotes');

  /*
   * The only fetch this component makes. The hook passes an `AbortSignal` and
   * guards on `signal.aborted` in both `.then` and `.catch`; it is skipped
   * entirely when nothing here can be edited, because a picker nobody can open
   * has no list to fill.
   */
  const { currencies, loading: currenciesLoading, failed: currenciesFailed } =
    useCurrencyOptions(canUpdate);

  /**
   * A refusal that named no field this section can mark.
   *
   * ⚠️ It is shown at SECTION level *as well as* on the field the person was
   * editing, not instead of it — those are two true things at once, and
   * coalescing them is the silent drop this repo keeps finding. The field says
   * why that save failed; this says the section sent a key the API no longer
   * accepts, which is a contract fault rather than a bad value and would
   * otherwise vanish with the next successful edit.
   */
  const [contractRefusal, setContractRefusal] = useState<string | null>(null);

  /** Two quick saves can resolve out of order; only the newest may publish. */
  const writeSeq = useRef(0);

  /**
   * Writes the given keys and reports what happened, in the vocabulary
   * `InlineField` renders. Nothing here throws: the field needs an outcome, not
   * an exception, and it has no business knowing what an `ApiError` is.
   *
   * `requestKeys` is what this particular patch carries — usually one key, and
   * two for the date pair and the currency/rate pair above.
   */
  const saveQuoteFields = async (
    patch: QuoteUpdateInput,
    requestKeys: readonly WriteKey[],
  ): Promise<InlineSaveResult> => {
    const ticket = writeSeq.current + 1;
    writeSeq.current = ticket;

    try {
      const response = await api.updateQuote(quote.id, patch);
      if (writeSeq.current === ticket) {
        setContractRefusal(null);
        /*
         * ⚠️ Inside the `writeSeq` guard, and BEFORE `onSaved`. Inside, because
         * a superseded save's report describes a version of the quote nobody is
         * looking at; before, because `onSaved` is what makes the page re-read
         * the quote, and a report handed up afterwards would land on a render
         * the refetch had already started.
         */
        onXeroPush?.(response.xeroPush);
        onSaved(response.data);
      }
      return { status: 'saved' };
    } catch (caught) {
      if (caught instanceof ApiError) {
        /*
         * ⚠️ REMOVED 2026-08-29, backend release `20260829011913-5fbab06`: the
         * branch that matched `QUOTE_ACCEPTANCE_CONFIRMATION_CODE` and reported
         * that the server had treated the patch as accepting the quote. That
         * 409 is gone from the server source and cannot fire on this route, so
         * the branch was a handler for a refusal that can no longer arrive —
         * which is worse than none, because it cannot be exercised and would
         * eventually fire on some unrelated 409. Same reasoning as the
         * unique-`quoteNumber` branch below.
         */
        /*
         * A 403 carries no `details`, so it can never be a field error — and it
         * can happen even though `canUpdate` said otherwise, because a role can
         * change under a live access token, which lasts over five days. The
         * hidden control is an optimisation; this is the guarantee.
         */
        if (caught.status === 403) {
          return {
            status: 'error',
            message: `${
              denialReason(ability, 'update', 'quotes') ??
              'Your account no longer has permission to change this quote.'
            } Nothing was saved.`,
          };
        }
        if (caught.status === 404) {
          return {
            status: 'error',
            message: 'This quote no longer exists, so nothing was saved.',
          };
        }
        /*
         * ⚠️ The unique-`quoteNumber` 409 branch that stood here is GONE with
         * the field. Nothing this section sends carries a UNIQUE index any
         * more — `xeroQuoteNumber` has no unique index and `xeroQuoteId` is
         * written by nothing, and neither is editable here regardless. A
         * handler for a refusal that cannot arrive is worse than none: it
         * cannot be exercised, so it rots unnoticed and then fires on some
         * unrelated 409, blaming a field the person never touched.
         */

        /*
         * Routed against this section's own SEND list rather than against the
         * shape of the path. A path can be non-empty and still bind to nothing
         * here — `stageId`, a total, `accountLeadId` — and that case looks
         * bindable, so it is the one that disappears silently.
         */
        const { fields, unroutable } = caught.routeFieldErrors(WRITE_KEYS);

        const routable: string[] = [];
        for (const key of requestKeys) {
          const message = fields[key];
          if (message) routable.push(message);
        }
        /*
         * A refusal naming a DIFFERENT field this section renders is named
         * rather than dropped: the date pair travels together, so editing the
         * issue date really can be refused about `validTill`, and a message
         * with no field attached would read as being about the box in front of
         * you.
         */
        for (const [key, message] of Object.entries(fields)) {
          if ((requestKeys as readonly string[]).includes(key)) continue;
          routable.push(`${FIELD_LABELS[key as WriteKey] ?? key}: ${message}`);
        }

        if (writeSeq.current === ticket) {
          setContractRefusal(unroutable.length > 0 ? deduped(unroutable).join(' ') : null);
        }

        /*
         * ⚠️ An unroutable rejection is reported as `warning`, not `error`, and
         * that is `InlineField`'s own contract rather than a preference here: it
         * is not a complaint about the value the user typed, so marking the
         * control invalid states something false about the draft and holding
         * the editor open invites a retype that will be refused identically.
         * `warning` closes the editor — the write is over — and keeps the
         * message on screen. A REAL field error still wins, because a 422 that
         * names something on this section is about the draft.
         */
        if (routable.length === 0 && unroutable.length > 0) {
          return { status: 'warning', message: deduped(unroutable).join(' ') };
        }

        /*
         * Both halves are rendered, never coalesced with `??`: a routable field
         * error and an unroutable rejection can be true at once, and dropping
         * either leaves the reader acting on half the refusal.
         */
        const all = deduped([...routable, ...unroutable]);
        return {
          status: 'error',
          message: all.length > 0 ? all.join(' ') : caught.message,
        };
      }
      return {
        status: 'error',
        message: 'Could not reach the server. Check your connection and try again.',
      };
    }
  };

  /*
   * The fields below are built by plain functions returning elements, never by
   * components declared in here: a component declared inside this one is a new
   * type on every render, so every field would be unmounted — losing its draft
   * and its "Saved" line — the moment anything else on this page changed.
   */

  /** A plain nullable text column. `''` clears it, as an explicit `null`. */
  const textField = (
    key: 'reference' | 'terms' | 'xeroTrackingLocation' | 'xeroTrackingTourType',
    label: string,
    extra: { multiline?: boolean; rows?: number; placeholder?: string; emptyLabel?: string } = {},
  ) => {
    const { multiline, ...rest } = extra;
    return (
      <InlineField
        label={label}
        value={quote[key] ?? ''}
        kind={multiline ? 'multiline' : 'text'}
        disabled={!canUpdate}
        maxLength={QUOTE_MAX_LENGTHS[key]}
        validate={(next) =>
          next.length > QUOTE_MAX_LENGTHS[key]
            ? `Must be ${QUOTE_MAX_LENGTHS[key].toLocaleString()} characters or fewer.`
            : null
        }
        onSave={(next) =>
          /*
           * Written out per key rather than computed, so a typo is a compile
           * error — and `null` rather than `''`, because across this API `null`
           * clears a column while an omitted key leaves it alone. `''` would be
           * stored as an empty string, which is a value.
           */
          saveQuoteFields(
            key === 'reference'
              ? { reference: next === '' ? null : next }
              : key === 'terms'
                ? { terms: next === '' ? null : next }
                : key === 'xeroTrackingLocation'
                  ? { xeroTrackingLocation: next === '' ? null : next }
                  : { xeroTrackingTourType: next === '' ? null : next },
            [key],
          )
        }
        {...rest}
      />
    );
  };

  /**
   * Subject — the one column here that is NOT nullable. `subject: null` is a
   * 422, so an emptied box is refused client-side rather than sent.
   */
  const subjectField = () => (
    <InlineField
      label="Subject"
      value={quote.subject}
      disabled={!canUpdate}
      maxLength={QUOTE_MAX_LENGTHS.subject}
      validate={(next) => {
        if (next === '') return 'Subject is required.';
        if (next.length > QUOTE_MAX_LENGTHS.subject) return 'Must be 255 characters or fewer.';
        return null;
      }}
      onSave={(next) => saveQuoteFields({ subject: next }, ['subject'])}
    />
  );

  /**
   * One of the co-validated date pair.
   *
   * ⚠️ Both keys go in every patch. See the module comment: the server only
   * compares the two when it can see both, so a one-sided PATCH silently
   * accepts an inconsistent quote.
   */
  const dateField = (key: 'issueDate' | 'validTill', label: string) => {
    const other = key === 'issueDate' ? quote.validTill : quote.issueDate;

    return (
      <InlineField
        label={label}
        value={quote[key] ?? ''}
        /*
         * A native date control, which is the pairing the modals already use
         * (`CycleFormModal`, `DealFormModal`) and is right here precisely
         * BECAUSE it never involves a `Date`: the element's value is
         * `YYYY-MM-DD` text, so nothing constructs `new Date('2026-05-31')` and
         * renders 30 May west of Greenwich. `validate` still runs the shape and
         * existence checks, because a part-typed date reads back as `''`.
         * Clearing IS legal on both of these columns — they are nullable — so a
         * blank commit is a real clear rather than a silent loss.
         */
        type="date"
        disabled={!canUpdate}
        emptyLabel={key === 'validTill' ? 'Open-ended' : 'Not set'}
        validate={(next) => {
          if (next === '') return null;
          // Shape AND existence, exactly as the server checks: 2026-02-30 is a 422.
          if (!isRealDate(next)) return 'Must be a real date in YYYY-MM-DD form.';
          if (other === null) return null;
          /*
           * Compared as strings, never as Dates. ISO dates sort
           * lexicographically so the comparison is exact, whereas parsing
           * 'YYYY-MM-DD' through `new Date()` reintroduces the timezone
           * off-by-one this format exists to avoid.
           */
          if (key === 'validTill' && other > next) {
            // The server's own wording, reported where the server reports it.
            return 'The quote cannot stop being valid before the date it was issued.';
          }
          if (key === 'issueDate' && next > other) {
            return `Valid till ${other}, so it cannot be issued after that.`;
          }
          return null;
        }}
        onSave={(next) => {
          const issueDate = key === 'issueDate' ? next : (quote.issueDate ?? '');
          const validTill = key === 'validTill' ? next : (quote.validTill ?? '');
          return saveQuoteFields(
            {
              issueDate: issueDate === '' ? null : issueDate,
              validTill: validTill === '' ? null : validTill,
            },
            ['issueDate', 'validTill'],
          );
        }}
      />
    );
  };

  /**
   * The currency picker.
   *
   * ⚠️ **The picker's VALUE is a `currencyCode` off a `/currencies` master row,
   * and it is WRITTEN under the key `currency`.** Neither name works in the
   * other place, and a currency id here would be stored as a number or coerced
   * into a nonsense code. `CHAR(3)`, `^[A-Z]{3}$`, and there is NO case folding
   * anywhere on this API — `"usd"` is a 422 — so it is uppercased on the way
   * out as well as matched uppercase.
   */
  const currencyField = () => {
    const stored = quote.currency;
    const options: InlineFieldOption[] = currencyOptions(currencies, stored);
    /*
     * The stored code stays selectable even when the master could not be read.
     * `currencyOptions` pins a missing code back only when it has a list to pin
     * it into, so an empty list would otherwise render this quote's real
     * currency as "Not set" — one stray commit away from clearing a field
     * nobody touched.
     */
    if (stored && !options.some((option) => option.value === stored)) {
      options.unshift({ value: stored, label: stored, hint: 'No longer offered' });
    }

    /*
     * The one thing a reader must know BEFORE choosing: this save carries a
     * second key. The failed-list case is said through `disabledReason`
     * instead — two different facts, so neither stands in for the other.
     */
    const pinnedRate = quote.currencyRate;
    const rateNote =
      pinnedRate === null
        ? null
        : `Changing the currency clears the pinned rate of ${pinnedRate}.`;

    return (
      <div className="grid min-w-0 gap-1">
        <InlineField
          kind="picker"
          label="Currency"
          value={stored ?? ''}
          /*
           * Disabled rather than merely empty. With no list, the only rows are
           * the stored code — re-picking which writes nothing — and "Not set",
           * which CLEARS a live currency. A control whose only working action is
           * destructive is worse than no control.
           */
          disabled={!canUpdate || currenciesFailed}
          /*
           * Only for the reason that is NOT the section-wide one. When the role
           * is what disables it, the sentence is said once above rather than
           * nine times; when the list failed, this field alone is unavailable
           * and the reader needs to know it is a failed lookup rather than a
           * lost permission — they would otherwise go hunting an administrator
           * for access that would change nothing.
           */
          {...(canUpdate && currenciesFailed
            ? {
                disabledReason:
                  'The currency list couldn’t be loaded. Reload the page to try again.',
              }
            : {})}
          options={options}
          optionsLoading={currenciesLoading}
          emptyOptionLabel="Not set"
          emptyLabel="Not set"
          placeholder={currenciesLoading ? 'Loading currencies…' : 'Not set'}
          searchPlaceholder="Search currencies…"
          {...(rateNote === null ? {} : { footnote: rateNote })}
          validate={(next) =>
            next !== '' && !CURRENCY_PATTERN.test(next.toUpperCase())
              ? 'Use a three-letter ISO code, e.g. USD.'
              : null
          }
          onSave={(next) => {
            const code = next === '' ? null : next.toUpperCase();
            /*
             * ⚠️ TWO keys, copied from `onCurrencyChange` in `QuoteFormModal`.
             * A pinned rate belongs to the currency it was pinned against, so
             * changing the currency clears it in the SAME patch — otherwise a
             * GBP rate stays on a USD document and is pushed to Xero verbatim.
             * The key is omitted entirely when there is no pin, so this stays a
             * one-key write on the ordinary quote.
             */
            const patch: QuoteUpdateInput =
              pinnedRate === null ? { currency: code } : { currency: code, currencyRate: null };
            return saveQuoteFields(
              patch,
              pinnedRate === null ? ['currency'] : ['currency', 'currencyRate'],
            );
          }}
        />
        {/*
          Said outside the popover as well as in it: a footnote only exists once
          the field has been opened, and the rate a save is about to clear is
          something to know BEFORE opening it.
        */}
        {rateNote !== null && canUpdate && !currenciesFailed && (
          <p role="status" className="m-0 text-2xs text-warning">
            {rateNote}
          </p>
        )}
      </div>
    );
  };

  /**
   * How Xero should present tax on this document.
   *
   * ⚠️ **`''` is not a fourth member of the enum.** It is the ABSENCE of a
   * value, stored as `null`, meaning *fall back to the company's Xero sales
   * default* — never "no tax", which is what `NOTAX` says. The empty row is
   * labelled with that sentence rather than "None" for exactly that reason.
   *
   * ⚠️ A DOCUMENT takes `NOTAX`; a COMPANY's `xeroSalesLineAmountType` takes
   * `NONE`. Same meaning, different word, two types — importing the company's
   * enum here would be a 422 on every save that used it.
   */
  const lineAmountTypeField = () => {
    const stored = quote.xeroLineAmountType ?? '';
    const label = documentLineAmountLabel(stored);
    return (
      <InlineField
        kind="picker"
        label="Tax treatment"
        value={stored}
        display={
          label === undefined ? (
            <span className="text-fg-subtle">{USE_COMPANY_DEFAULT}</span>
          ) : (
            label
          )
        }
        disabled={!canUpdate}
        options={DOCUMENT_LINE_AMOUNT_OPTIONS.map((option) => ({
          value: option.value,
          label: option.label,
        }))}
        emptyOptionLabel={USE_COMPANY_DEFAULT}
        placeholder={USE_COMPANY_DEFAULT}
        onSave={(next) =>
          saveQuoteFields(
            /*
             * Narrowed, never cast. The control can only produce the three
             * DOCUMENT members, but a value outside the union must not be
             * smuggled into a write claiming to be one — anything unrecognised
             * becomes `null`, the honest reading of an empty control.
             */
            { xeroLineAmountType: toDocumentLineAmountType(next) },
            ['xeroLineAmountType'],
          )
        }
      />
    );
  };

  return (
    <div className="grid min-w-0 gap-4">
      {contractRefusal !== null && (
        <p
          role="status"
          className={cx(
            'm-0 rounded-md border border-danger-border bg-danger-soft px-3 py-2',
            'text-xs [overflow-wrap:anywhere] text-danger',
          )}
        >
          The server refused something not shown on this screen: {contractRefusal}
        </p>
      )}

      {readOnlyReason !== null && (
        <p role="status" className="m-0 text-xs text-fg-muted">
          {readOnlyReason}
        </p>
      )}

      <dl className="m-0 grid min-w-0 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
        <DetailItem label="Subject" className="sm:col-span-2 lg:col-span-3">
          {subjectField()}
        </DetailItem>

        {/*
          ⚠️ READ-ONLY since 2026-08-27, and it was editable here for a few
          hours. `quoteNumber` is allocated by the SERVER and is now a 422 on
          BOTH create and PATCH — `path: "quoteNumber"`, measured on the wire —
          so an editor here could only ever refuse. The owner's ruling was
          close-the-write-paths-now, ledger later.

          It is shown rather than dropped: a quote that HAS a number still has
          the reference people read out, and an absent field would read as the
          number being gone rather than as it not being ours to set.

          ⚠️ **A quote created from here now carries NO number at all** until the
          ledger exists. That is a known gap, not a bug — do not "fix" it by
          allocating one client-side, which is a read-then-write race the UNIQUE
          index turns into a 409 on a field the losing user never touched.
        */}
        {/*
          ⚠️ "Not allocated" is worded to MATCH `QuoteFormModal`'s read-only
          field exactly. The two surfaces show one column, and they briefly
          disagreed: this said "Not allocated yet", which **promises a number is
          coming**. Nothing may promise the ledger — it is requested and
          unbuilt, and copy that implies otherwise sends someone looking for a
          number that is not going to appear. "Not allocated" is the state; the
          ledger is a plan.
        */}
        <DetailItem label="Quote number">
          {quote.quoteNumber ? (
            <span className="tabular-nums">{quote.quoteNumber}</span>
          ) : (
            <span className="text-fg-subtle">Not allocated</span>
          )}
        </DetailItem>

        {/*
          ⚠️ Labelled as the CUSTOMER's, never "our reference". It is the PO
          number their finance team matches the payment against, and Xero prints
          it. Echoing `quoteNumber` into it puts our reference on their document.
        */}
        <DetailItem label="Customer PO number">
          {textField('reference', 'Customer PO number', {
            emptyLabel: 'Not recorded',
          })}
        </DetailItem>

        {/*
          Read-only, and shown rather than omitted so nobody hunts for a control
          that was deliberately not built. See the module comment.
        */}
        <DetailItem label="Stage">
          {quote.stage ? (
            quote.stage.name
          ) : (
            <span className="text-fg-subtle">Not set</span>
          )}
        </DetailItem>

        {/*
          ⚠️ READ-ONLY BY CHOICE, and structurally enforced: `accountLead` is
          the RESOLVED value — this quote's own override where it has one,
          otherwise its company's — and it is on no quote input type in this
          app, so `saveQuoteFields` could not express it even if something here
          tried. It is deliberately NOT wrapped in an `InlineField`: that
          control offers to save, and nothing here is meant to save a lead.

          ⚠️ **CORRECTION (2026-08-30): this read "structurally so rather than
          by choice … `accountLeadId` is a 422 in a quote write body", and the
          422 claim is FALSE.** Migration 082 added `d_quotes.account_lead_id`
          and the key is on the write schema as `optionalUserId()`
          (`quotes.schema.ts:211`, verified from backend source); `null` clears
          the override rather than blanking the field. It is refused only on a
          DELIVERABLE. So the ordering was exactly backwards: it is read-only
          BY CHOICE — the owner retired lead-assignment on quotes in favour of
          the company's — and structural only as a guard we chose to keep. The
          rendering stands; treat an override as unbuilt, not as forbidden.

          ⚠️ It is NOT the owner under a new name. The two are different people
          (quote 9: owner Paras Fatnani, account lead Daryna Kodinets), and
          this section never showed an owner — so this label must never be
          read, or reworded, as "owner". To CHANGE it, change the account lead
          on the company; every quote that company holds follows immediately.

          `null` is a real state and says only that, neutrally: the company may
          genuinely have no lead, or the quote may have no company. Neither is
          this section's to assert.
        */}
        <DetailItem label="Account lead">
          <StaffValue person={quote.accountLead} />
        </DetailItem>

        <DetailItem label="Issue date">{dateField('issueDate', 'Issue date')}</DetailItem>
        <DetailItem label="Valid till">{dateField('validTill', 'Valid till')}</DetailItem>
        <DetailItem label="Currency">{currencyField()}</DetailItem>
        <DetailItem label="Tax treatment">{lineAmountTypeField()}</DetailItem>

        {/*
          ⚠️ An empty box here means DERIVE — from the line's own project, or the
          quote's when the line names none — and never "not tracked". The API
          cannot express untracked, so nothing in this block may imply these two
          can turn tracking off. A document that derives nothing simply pushes
          untracked, which is an ordinary outcome rather than a refusal.
        */}
        <DetailItem label="Location override">
          {textField('xeroTrackingLocation', 'Xero Location override', {
            emptyLabel: 'Derived from the project',
          })}
        </DetailItem>

        <DetailItem label="Tour Type override">
          {textField('xeroTrackingTourType', 'Xero Tour Type override', {
            emptyLabel: 'Derived from the project',
          })}
        </DetailItem>

        <DetailItem label="Terms" className="sm:col-span-2 lg:col-span-3">
          {textField('terms', 'Terms', {
            multiline: true,
            rows: 6,
            emptyLabel: 'No terms recorded',
          })}
        </DetailItem>
      </dl>

      {/*
        Said once, plainly, rather than on each of the two boxes: they fail in
        OPPOSITE directions and a typo in the second is not recoverable from this
        app at all.
      */}
      {canUpdate && (
        <p
          className={cx(
            'm-0 rounded-md border border-warning-border bg-warning-soft px-3 py-2',
            'text-xs break-words text-warning',
          )}
        >
          Free text, checked only at the push: a{' '}
          <strong className="font-semibold">Location</strong> Xero does not have refuses the push,
          while a <strong className="font-semibold">Tour Type</strong> it does not have is{' '}
          <strong className="font-semibold">created there</strong>.
        </p>
      )}

      {/*
        ⚠️ THE FOUR TOTALS ARE DELIBERATELY NOT RENDERED HERE, and this comment
        is the reason nobody should add them back.

        They ARE shown on `/quotes/:id`, in the page's own "Totals" group, and
        that version is strictly richer: every figure carries its CURRENCY CODE
        (through `InvoiceMoney`), and the group also holds three things this
        component cannot see — the linked deal's own value, the grand total
        converted into Xero's base currency, and the exchange rate behind that
        conversion.

        A read-only copy here was tried and removed. It rendered the same four
        numbers through `formatDecimalString` with NO currency code, directly
        above the page's group — so a quote showed its money twice, and the
        upper, code-less copy read first. On a system where currency is
        per-document and never converted, a bare figure is the one rendering of
        money this app treats as wrong by construction.

        The rule that generalises: a component mounted INSIDE a page's panel
        must not re-render what the panel already shows. Where both could, the
        one that can see more of the record wins.
      */}
    </div>
  );
}

/**
 * A `d_users` row — SEED EMERGE staff, never a contact.
 *
 * ⚠️ **`name` can be `''` on a real person** (`KanbanOwner` documents the same
 * hazard), and an empty string is falsy in a template but truthy as a rendered
 * node — `{person.name}` on such a row prints a blank cell that reads exactly
 * like "nobody", which is the one thing this value must never say wrongly. So
 * it falls back: name, then email, then `Staff #id`, which always exists. Only
 * a genuinely absent person renders the neutral empty state.
 *
 * It renders text alone, with no `Avatar`: the detail pages pair one with a
 * name, but an avatar built from an EMPTY name is a blank circle, and the whole
 * point of the fallback chain is that this value never renders blank.
 */
function StaffValue({ person }: { person: UserSummary | null }) {
  if (person === null) return <span className="text-fg-subtle">Not set</span>;
  const shown = person.name !== '' ? person.name : person.email !== '' ? person.email : null;
  return <span className="truncate">{shown ?? `Staff #${person.id}`}</span>;
}

/**
 * One label/value pair. A `<div>` inside a `<dl>` is valid and keeps the pair
 * together.
 *
 * The `<dt>` is NOT a label — `InlineField` names its own control from the
 * `label` prop it is given, which is why several fields pass a longer, more
 * specific name than the `<dt>` shows.
 */
function DetailItem({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cx('min-w-0', className)}>
      <dt className="text-2xs font-semibold tracking-[0.06em] text-fg-subtle uppercase">{label}</dt>
      <dd className="m-0 mt-1 text-sm text-fg-secondary [overflow-wrap:anywhere]">{children}</dd>
    </div>
  );
}
